<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__code_input__title') }}
        </p>
        <p
          class="kds-typography-paragraph-large"
          v-html="description"
        />
      </div>
      <form
        id="activation-code-form"
        class="kds-content-block"
        @submit.prevent="handleSubmitEvent"
      >
        <TextField
          v-bind="fieldAttrs.activationCode"
          :label="$t('eu__code_input__code_label')"
          :hint="codeFormatHint"
          data-qa="activation-code-input"
          style="text-transform: uppercase"
        />
      </form>
      <BaseBanner
        v-if="status === CODE_INPUT_STATUS.ERROR && errorMessage"
        class="kds-content-block"
        data-qa="activation-code-error"
        :title="errorMessage.title"
        variant="warning"
      >
        <p
          class="kds-typography-paragraph-medium"
          v-html="errorMessage.description(form.activationCode)"
        />
        <template
          v-if="errorMessage.cta"
          #actions
        >
          <BaseButton
            size="small"
            variant="outlined"
            :text="errorMessage.cta"
            @click="handleErrorQuickActionClick"
          />
        </template>
      </BaseBanner>
      <BaseBanner
        v-else-if="status === CODE_INPUT_STATUS.SUCCESS"
        class="kds-content-block"
        data-qa="activation-code-success"
        :title="successMessage.title"
      >
        <p
          class="kds-typography-paragraph-medium"
          v-html="successMessage.description"
        />
      </BaseBanner>
      <BaseBottomSheet
        :title="$t('eu__code_input__help_title')"
        screen-name="unified_code_input_help_bottom_sheet"
      >
        <template #trigger="{ open }">
          <BaseLink
            :text="$t('eu__code_input__help_trigger')"
            @click="open"
          >
            <template #icon>
              <InfoIcon />
            </template>
          </BaseLink>
        </template>
        <div v-html="$t('eu__code_input__help_content')" />
      </BaseBottomSheet>
      <BaseBanner
        v-if="showConsents && consentError"
        data-qa="consents-error"
        :title="$t('generic__error_title')"
        variant="warning"
      >
        <p
          class="kds-typography-paragraph-medium"
          v-html="
            $t('generic__error_message', { email: env.VITE_SUPPORT_EMAIL })
          "
        />
      </BaseBanner>
      <BaseBanner
        v-if="showConsents"
        variant="info"
      >
        <div class="kds-content-block">
          <CheckboxField
            data-qa="accept-terms-and-conditions"
            form="activation-code-form"
            v-bind="fieldAttrs.acceptTerms"
          >
            <DocumentViewer :link="termsAndConditionsLinks" />
          </CheckboxField>
          <CheckboxField
            data-qa="accept-data"
            form="activation-code-form"
            v-bind="fieldAttrs.acceptData"
          >
            <div
              v-html="
                $t('eu__health_data_agreement_v9.5.0', { app_name: appName })
              "
            />
          </CheckboxField>
          <CheckboxField
            data-qa="accept-tracking"
            form="activation-code-form"
            v-bind="fieldAttrs.acceptTracking"
          >
            <div
              v-html="$t('eu__accept_tracking_v9.5.0', { app_name: appName })"
            />
          </CheckboxField>
        </div>
      </BaseBanner>
    </div>
    <template #actions>
      <BaseButton
        type="submit"
        :loading="submitAttrs.loading"
        :text="mainCtaText"
        data-qa="activation-code-main-cta"
        form="activation-code-form"
        @click="handleSubmitEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { escapeHtml } from '@shared/utils/escapeHtml'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseBottomSheet from '@shared/components/BaseBottomSheet.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseLink from '@shared/components/BaseLink.vue'
import TextField from '@shared/components/form/TextField.vue'
import CheckboxField from '@shared/components/form/CheckboxField.vue'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import InfoIcon from '@shared/assets/icon/InfoIcon.vue'
import useForm from '@shared/composables/useForm.js'
import useEuStore from '@eu/app/stores/useEuStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useSharedStore from '@shared/stores/useSharedStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import * as apiRequest from '@shared/api-endpoints/index.js'
import env from '@shared/env.js'
import ROUTE from '@eu/app/router/appModule/names.js'
import { useRoute } from 'vue-router'
import { useI18n } from 'petite-vue-i18n'
import Tracker from '@shared/Tracker.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import defineLanguage from '@shared/utils/defineLanguage'

const route = useRoute()
const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.CODE_INPUT,
  ROUTE.INSURANCE_VERIFICATION_PRIVATE,
  ROUTE.PRESCRIPTION_ACTIVATION,
)

const euStore = useEuStore()
const sharedStore = useSharedStore()
const corporateStore = useCorporateStore()
const userStore = useUserStore()
const { t } = useI18n()

const appName = computed(() => t('eu__generic__app'))
const user = computed(() => userStore.user)
const hasAcceptedDataProtection = computed(
  () => user.value?.consents?.health_data === true,
)
const termsAndConditionsLinks = computed(() => {
  const base = env.VITE_PUBLIC_PATH
  const documentLang = defineLanguage(userStore.lang)
  const termsLink = `${base}static/${documentLang}/terms/index.html`
  const policyLink = `${base}static/${documentLang}/privacy/index.html`
  return t('accept_terms_and_conditions_eu', { termsLink, policyLink })
})

const CODE_INPUT_STATUS = {
  IDLE: 'idle',
  ERROR: 'error',
  SUCCESS: 'success',
}

const supportEmailParams = {
  support_email: env.VITE_SUPPORT_EMAIL,
}

const ERROR_MESSAGES = {
  DEFAULT: {
    title: t('eu__code_input__error__default_title'),
    description: () =>
      t('eu__code_input__error__default_description', supportEmailParams),
  },
  SERVER_ERROR: {
    title: t('eu__code_input__error__server_error_title'),
    description: () =>
      t('eu__code_input__error__server_error_description', supportEmailParams),
  },
  PAYER_ERROR: {
    title: t('eu__code_input__error__payer_error_title'),
    description: () =>
      t('eu__code_input__error__payer_error_description', supportEmailParams),
  },
  UNKNOWN_PAYER: {
    title: t('eu__code_input__error__unknown_payer_title'),
    description: () =>
      t('eu__code_input__error__unknown_payer_description', supportEmailParams),
  },
  CODE_IS_PZN: {
    title: t('eu__code_input__error__code_is_pzn_title'),
    description: () => t('eu__code_input__error__code_is_pzn_description'),
    cta: t('eu__code_input__error__code_is_pzn_cta'),
    action: () => {
      apiRequest.sendSubscriptionActivationEntry({
        type: 'user_has_no_code',
      })
      apiRequest.sendSubscriptionActivationEntry({
        type: 'user_has_prescription',
      })
      navigateNext('pzn')
    },
  },

  CODE_WITH_INVALID_CHARACTERS: {
    title: t('eu__code_input__error__code_with_invalid_characters_title'),
    description: () =>
      t('eu__code_input__error__code_with_invalid_characters_description', {
        code: replaceInvalidCharacters(form.value.activationCode),
      }),
    cta: t('eu__code_input__error__code_with_invalid_characters_cta'),
    action: () => {
      form.value.activationCode = replaceInvalidCharacters(
        form.value.activationCode,
      )
    },
  },
  CODE_ALREADY_USED: {
    title: t('eu__code_input__error__code_already_used_title'),
    description: () =>
      t(
        'eu__code_input__error__code_already_used_description',
        supportEmailParams,
      ),
  },
  CODE_EXPIRED: {
    title: t('eu__code_input__error__code_expired_title'),
    description: () =>
      t('eu__code_input__error__code_expired_description', supportEmailParams),
  },
  CODE_ISSUED_FOR_OTHER_APP: {
    title: t('eu__code_input__error__code_issued_for_other_app_title'),
    description: () =>
      t(
        'eu__code_input__error__code_issued_for_other_app_description',
        supportEmailParams,
      ),
  },
  CODE_CANCELLED: {
    title: t('eu__code_input__error__code_cancelled_title'),
    description: () => t('eu__code_input__error__code_cancelled_description'),
  },
}

const status = ref(CODE_INPUT_STATUS.IDLE)
const errorCode = ref('')

const showConsents = computed(
  () =>
    status.value === CODE_INPUT_STATUS.IDLE && !hasAcceptedDataProtection.value,
)

const initialParticipationId =
  corporateStore.corporate?.key === 'generalihealthsolutions'
    ? sharedStore.initialQuery?.participation_id
    : ''

const form = ref({
  activationCode: String(
    initialParticipationId ||
      euStore.formData.prescriptionCode ||
      sharedStore.initialQuery?.voucher ||
      '',
  ).toUpperCase(),
  acceptTerms: false,
  acceptData: false,
  acceptTracking: user.value?.consents?.tracking === true,
})
const formConfig = computed(() => ({
  activationCode: { validations: ['required'] },
  ...(showConsents.value
    ? {
        acceptTerms: { validations: ['accepted'] },
        acceptData: { validations: ['accepted'] },
        acceptTracking: { validations: [] },
      }
    : {}),
}))

const consentError = ref(false)

const saveConsents = () =>
  apiRequest.updateUserConsents([
    { type: 'terms', given: true },
    { type: 'health_data', given: true },
    { type: 'tracking', given: form.value.acceptTracking },
  ])

const errorMessage = computed(() => {
  // Prescription upload is only available to strictly public insurers. For
  // other corporates, let the user correct the code or contact support.
  if (
    errorCode.value === 'CODE_IS_PZN' &&
    corporateStore.corporate?.insuranceType !== 'public'
  ) {
    return {
      title: ERROR_MESSAGES.CODE_IS_PZN.title,
      description: ERROR_MESSAGES.DEFAULT.description,
    }
  }
  return ERROR_MESSAGES[errorCode.value] || ERROR_MESSAGES.DEFAULT
})
const corporate = computed(() => corporateStore.corporate)
const isPrivateInsuranceRoute = computed(
  () => route.name === ROUTE.INSURANCE_VERIFICATION_PRIVATE,
)

const description = computed(() => {
  if (!isPrivateInsuranceRoute.value) return t('eu__code_input__description')
  if (sharedStore.initialQuery?.viewType === 'subscription_renewal') {
    return t('eu__subscription_renewal__private__description_v9.2.0', {
      app_name: t('eu__generic__app'),
    })
  }
  switch (corporate.value?.key) {
    case 'carelutions':
    case 'dkv':
    case 'generalihealthsolutions':
      return t(
        'eu__insurance_verification__private__description__group1_v9.1.0',
      )
    default:
      return t(
        'eu__insurance_verification__private__description__group5_v9.1.0',
      )
  }
})

const codeFormatHint = computed(() => {
  if (!isPrivateInsuranceRoute.value) return null
  switch (corporate.value?.key) {
    case 'generalihealthsolutions':
      return `${t('eu__generic_example_abbreviation')} KAI_XXXX_XXXX, ROT_XXXX_XXXX`
    case 'carelutions':
      return `${t('eu__generic_example_abbreviation')} WIDE-XXXX-XXXX`
    default:
      return `${t('eu__generic_example_abbreviation')} XXXX XXXX`
  }
})

const successMessage = computed(() => {
  if (corporate.value?.isPublicInsurance) {
    return {
      title: t('eu__code_input__success__title'),
      description: t('eu__code_input__success__description__public', {
        corporate_title: escapeHtml(corporate.value.title),
      }),
    }
  }

  return {
    title: t('eu__code_input__success__title'),
    description: t('eu__code_input__success__description__generic'),
  }
})
const mainCtaText = computed(() =>
  status.value === CODE_INPUT_STATUS.SUCCESS
    ? t('eu__code_input__main_cta__success')
    : t('eu__code_input__main_cta__idle'),
)

const trackCodeInputSubmitOutcome = ({ outcome, trackedErrorCode = null }) => {
  const activationStatus = outcome === 'success' ? 'success' : 'failed'
  Tracker.trackButtonSelect(`submit_${outcome}`, 'unified_code_input', {
    error_code: trackedErrorCode,
  })
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.activate_subscription',
      action: 'view',
      object_type: 'screen',
      screen_name: 'unified_code_input',
    },
    { activate_subscription_status: activationStatus },
  )
}

const { fieldAttrs, submitAttrs, submit } = useForm(form, formConfig, {
  onSubmit: async () => {
    status.value = CODE_INPUT_STATUS.IDLE
    errorCode.value = ''
    consentError.value = false
    if (showConsents.value) {
      try {
        await saveConsents()
      } catch (e) {
        consentError.value = true
        throw e
      }
    }
    await apiRequest.submitActivationCode(form.value.activationCode.trim())
    sharedStore.digaActivated = true
    status.value = CODE_INPUT_STATUS.SUCCESS
    trackCodeInputSubmitOutcome({ outcome: 'success' })
  },
  onSuccess: async () => {},
  onError: (error) => {
    if (consentError.value) {
      Tracker.trackButtonSelect('consents_error', 'unified_code_input', {
        error_code: error?.response?.status || 'unknown',
      })
      return {}
    }
    status.value = CODE_INPUT_STATUS.ERROR
    errorCode.value = (error?.response?.data?.error || 'DEFAULT').toUpperCase()
    trackCodeInputSubmitOutcome({
      outcome: 'error',
      trackedErrorCode: errorCode.value,
    })
    return { activationCode: true }
  },
})

watch(
  () => form.value.activationCode,
  (newCode, oldCode) => {
    const uppercaseCode = String(newCode || '').toUpperCase()

    if (newCode !== uppercaseCode) {
      form.value.activationCode = uppercaseCode
      return
    }

    euStore.updateFormData({ prescriptionCode: uppercaseCode })
    if (uppercaseCode !== oldCode && status.value === CODE_INPUT_STATUS.ERROR) {
      status.value = CODE_INPUT_STATUS.IDLE
      errorCode.value = ''
    }
  },
)

const handleSubmitEvent = async () => {
  if (status.value === CODE_INPUT_STATUS.SUCCESS) {
    navigateNext('activated')
    return
  }
  await submit()
}

const handleErrorQuickActionClick = () => {
  if (errorMessage.value?.action == null) return

  Tracker.trackButtonSelect(
    String(errorCode.value || '').toLowerCase(),
    'unified_code_input_error',
    { error_code: errorCode.value },
  )

  errorMessage.value.action()
}

const replaceInvalidCharacters = (code) =>
  code.replace(/0/g, 'O').replace(/1/g, 'I')
</script>
